import { useEffect, useState } from 'react'

import { estimateZoomFactor, isZoomedPastThreshold } from '../utils/zoomEstimate'
import {
  getVisibleBox,
  isOverflowing,
  isRectReachable,
} from '../utils/viewportReachability'
import { getAnchors, subscribeAnchors } from '../utils/chromeAnchors'

/**
 * Why the indicator is showing. The message no longer varies by arm — it is one consistent
 * sentence — but which signal fired is still what decides whether to show at all, and is worth
 * keeping named rather than collapsing to a bare boolean.
 */
export type ZoomIndicatorReason = 'zoomed' | 'clipped'

/** Sub-pixel and scrollbar slop that must not trip the indicator. */
const RECT_TOLERANCE_PX = 2

/**
 * Anchors mount mid-animation (the bottom bar slides in, and its lazy chunk lands at an
 * unpredictable moment), so their first rects are not final. Evaluating them immediately flashes
 * the indicator on a layout that is about to settle.
 */
const ANCHOR_SETTLE_MS = 500

/** The condition must persist before it surfaces, so a transient reflow does not blink a warning. */
const SHOW_DEBOUNCE_MS = 300

/**
 * SC-255 — should the zoom indicator be showing, and on the strength of which signal?
 *
 * A single OR: the browser is zoomed past the threshold, **or** anchored chrome is fully or
 * partially outside the visible viewport. Zoom alone catches surfaces we do not measure (and
 * zoom persisted from a previous session, where there is no event to hook); the reachability
 * half catches clipping the estimators would miss.
 *
 * Pass `enabled: false` outside the game and no listeners are attached at all.
 */
export function useZoomIndicatorState(enabled: boolean): ZoomIndicatorReason | null {
  const [reason, setReason] = useState<ZoomIndicatorReason | null>(null)

  useEffect(() => {
    if (!enabled) {
      setReason(null)
      return
    }

    let rafId = 0
    let evaluatePending = false
    let showTimerId = 0
    let settleTimerId = 0
    /** Anchor rects are not trusted until this moment. */
    let settledAt = Date.now() + ANCHOR_SETTLE_MS

    /**
     * Which arm fired, or `null` for neither.
     *
     * The two are reported separately because only one of them is proof of zoom: the width ratio
     * behind `estimateZoomFactor` also moves when browser chrome eats horizontal space — a docked
     * sidebar or devtools panel — so `clipped` means "a control is unreachable" and nothing more.
     *
     * The **copy** no longer distinguishes them. An earlier revision gave `clipped` cause-neutral
     * wording precisely because the shortcut might change nothing; that was replaced by one
     * consistent sentence on a deliberate product call (Prashanth, 2026-09-03) — two near-identical
     * warnings were judged worse than occasionally naming a shortcut that does not help. Keep the
     * distinction here rather than collapsing to a boolean: it is what the decision would be
     * reversed against, and it is worth being able to tell the two causes apart when debugging.
     */
    const conditionReason = (): ZoomIndicatorReason | null => {
      const vv = window.visualViewport ?? undefined

      if (isZoomedPastThreshold(estimateZoomFactor(vv, window))) return 'zoomed'

      const box = getVisibleBox(vv, window)
      const clipped = getAnchors().some(([, el]) => {
        // A node React has already detached still sits in the registry until its ref fires.
        if (!el.isConnected) return false
        if (isOverflowing(el, RECT_TOLERANCE_PX)) return true
        return !isRectReachable(el.getBoundingClientRect(), box, RECT_TOLERANCE_PX)
      })

      return clipped ? 'clipped' : null
    }

    const evaluate = () => {
      if (Date.now() < settledAt) return

      if (conditionReason()) {
        if (showTimerId) return
        showTimerId = window.setTimeout(() => {
          showTimerId = 0
          // Re-check rather than trusting the reading from 300ms ago — and take the reason from
          // this reading, since the arm that fires can change while the debounce runs.
          setReason(conditionReason())
        }, SHOW_DEBOUNCE_MS)
        return
      }

      // Clearing is immediate: the moment controls are reachable again the message is wrong.
      if (showTimerId) {
        window.clearTimeout(showTimerId)
        showTimerId = 0
      }
      setReason(null)
    }

    /**
     * Coalesced to one evaluation per frame — `getBoundingClientRect()` forces a layout flush and
     * a pinch gesture emits ~60 events per second.
     *
     * The `evaluatePending` flag is cleared inside the callback rather than by reading `rafId`
     * back: were the coalescing keyed on the handle, a callback that runs before the assignment
     * completes would leave a stale non-zero handle behind and silently swallow every later
     * scheduling attempt.
     */
    const schedule = () => {
      if (evaluatePending) return
      evaluatePending = true
      rafId = window.requestAnimationFrame(() => {
        evaluatePending = false
        rafId = 0
        evaluate()
      })
    }

    const deferPastSettle = () => {
      settledAt = Date.now() + ANCHOR_SETTLE_MS
      if (settleTimerId) window.clearTimeout(settleTimerId)
      settleTimerId = window.setTimeout(() => {
        settleTimerId = 0
        schedule()
      }, ANCHOR_SETTLE_MS + 16)
    }

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)

    const observeAnchors = () => {
      if (!observer) return
      observer.disconnect()
      getAnchors().forEach(([, el]) => observer.observe(el))
    }

    const onAnchorsChanged = () => {
      observeAnchors()
      deferPastSettle()
    }

    observeAnchors()
    deferPastSettle()

    const unsubscribe = subscribeAnchors(onAnchorsChanged)
    window.addEventListener('resize', schedule)
    // Page zoom fires `resize`; WebKit magnification and the panning after it only fire these.
    window.visualViewport?.addEventListener('resize', schedule)
    window.visualViewport?.addEventListener('scroll', schedule)

    return () => {
      unsubscribe()
      window.removeEventListener('resize', schedule)
      window.visualViewport?.removeEventListener('resize', schedule)
      window.visualViewport?.removeEventListener('scroll', schedule)
      observer?.disconnect()
      if (rafId) window.cancelAnimationFrame(rafId)
      if (showTimerId) window.clearTimeout(showTimerId)
      if (settleTimerId) window.clearTimeout(settleTimerId)
    }
  }, [enabled])

  return reason
}
